import { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT, useI18n } from "../context/I18nContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import BuyCreditsButton from "../components/BuyCreditsButton";
import {
  Upload, Sparkles, Target, CheckCircle2, AlertTriangle,
  Loader2, Wand2, ArrowRight, Zap, ShieldCheck, FileText, TrendingUp
} from "lucide-react";

export default function JobMatch() {
  const { user, refresh } = useAuth();
  const t = useT();
  const { lang } = useI18n();

  const [file, setFile] = useState(null);
  const [jobDesc, setJobDesc] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);      // { id, job_title, analysis, credits }
  const [improving, setImproving] = useState(false);
  const [improved, setImproved] = useState(null);       // improved CV data (from AI)
  const [savedResult, setSavedResult] = useState(null); // { download_url, title, credits }

  const missingFile = !file;
  const jdTooShort = jobDesc.trim().length < 30;
  const outOfCredits = (user?.credits ?? 0) < 1;
  const disabledReason = analyzing
    ? null
    : missingFile ? t("job.needCv")
    : jdTooShort ? t("job.jdShort")
    : outOfCredits ? t("toast.needCredits")
    : null;

  const submit = async (e) => {
    e?.preventDefault();
    if (missingFile) return toast.error(t("job.needCv"));
    if (jdTooShort) return toast.error(t("job.jdShort"));
    if (outOfCredits) return toast.error(t("toast.needCredits"));

    setAnalyzing(true);
    setAnalysis(null);
    setImproved(null);
    setSavedResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("job_description", jobDesc.trim());
      fd.append("lang", lang);
      const r = await api.post("/analyze/cv-job", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setAnalysis(r.data);
      toast.success(t("job.analyzed"));
      await refresh();
      window.scrollTo({ top: 400, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || t("job.errorNoCredit"));
    } finally {
      setAnalyzing(false);
    }
  };

  const improve = async () => {
    if (!analysis) return;
    // Build a "current CV" from the AI's extraction. For MVP we ask backend to work from the raw text again.
    // Cleanest: pull the raw analysis job_desc + let backend re-parse. Instead, we re-extract via /cv/extract first
    // to get a proper CVData JSON, then send that + JD to /analyze/improve-cv.
    if ((user?.credits ?? 0) < 1) return toast.error(t("toast.needCredits"));
    setImproving(true);
    try {
      // Step 1: extract structured CV from the same file
      const fd = new FormData();
      fd.append("file", file);
      const rExtract = await api.post("/cv/extract", fd, { headers: { "Content-Type": "multipart/form-data" }});
      // Step 2: improve
      const r = await api.post("/analyze/improve-cv", { data: rExtract.data, job_description: jobDesc, lang });
      setImproved(r.data.improved);
      toast.success(t("job.improved"));
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("job.errorNoCredit"));
    } finally {
      setImproving(false);
    }
  };

  const useImproved = async () => {
    if (!improved) return;
    try {
      const title = analysis?.job_title ? `${analysis.job_title} — CV` : "Improved CV";
      const r = await api.post("/cv/save-improved", { data: improved, template: "classic", title });
      setSavedResult(r.data);
      toast.success(t("job.saved"));
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="paper-bg min-h-[calc(100vh-4rem)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="job-match-page">

        {/* Header */}
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div className="max-w-2xl">
            <div className="pill mb-3"><Target size={12}/> {t("job.badge")}</div>
            <h1 className="font-serif-display text-4xl font-bold text-[#0F172A]">{t("job.title")}</h1>
            <p className="text-[#475569] mt-2 leading-relaxed">{t("job.subtitle")}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="pill" data-testid="jm-credit-pill">{user?.credits ?? 0} {t("common.credits")}</span>
            <BuyCreditsButton onSuccess={refresh}/>
          </div>
        </div>

        {/* Input row */}
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <label
              className="card-classic p-6 border-dashed border-2 bg-white flex flex-col items-center justify-center text-center cursor-pointer hover:border-[#0F172A] block"
              data-testid="jm-upload-zone"
              style={{ borderColor: file ? "#0F172A" : "#E2DBCE" }}
            >
              <div className="w-12 h-12 rounded-full bg-[#0F172A] text-white grid place-items-center mb-3">
                <Upload size={20}/>
              </div>
              <div className="font-serif-display text-lg">{file ? file.name : t("job.uploadCv")}</div>
              <div className="text-xs text-[#64748B] mt-1">{t("job.uploadHint")}</div>
              <input type="file" className="hidden" accept=".pdf,.docx,.png,.jpg,.jpeg,.webp"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="jm-file-input"/>
            </label>

            <div className="card-classic p-4">
              <div className="font-mono-jb text-xs uppercase tracking-wider text-[#64748B] mb-2">{t("job.step1Label")}</div>
              <ol className="space-y-2 text-sm text-[#475569]">
                <li className="flex gap-2"><span className="pill flex-shrink-0">1</span>{t("job.step1")}</li>
                <li className="flex gap-2"><span className="pill flex-shrink-0">2</span>{t("job.step2")}</li>
                <li className="flex gap-2"><span className="pill flex-shrink-0">3</span>{t("job.step3")}</li>
              </ol>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="card-classic p-5">
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="jd" className="font-serif-display text-lg font-semibold">{t("job.jdLabel")}</label>
                <span className="text-xs text-[#64748B]">{jobDesc.length} chars</span>
              </div>
              <Textarea
                id="jd"
                rows={12}
                value={jobDesc}
                onChange={(e) => setJobDesc(e.target.value)}
                placeholder={t("job.jdPlaceholder")}
                data-testid="jm-jd-input"
                className="font-sans text-sm leading-6"
              />
              <div className="mt-4 flex items-center gap-3">
                <Button
                  onClick={submit}
                  disabled={analyzing}
                  className="bg-[#0F172A] hover:bg-[#1E293B] text-white h-11 flex-1"
                  data-testid="jm-analyze-button"
                >
                  {analyzing ? (<><Loader2 size={16} className="mr-2 animate-spin"/> {t("job.analyzing")}</>) :
                    (<><Sparkles size={16} className="mr-2"/> {t("job.analyzeBtn")}</>)}
                </Button>
                <span className="text-xs text-[#64748B]">{t("job.costLine")}</span>
              </div>
              {disabledReason && (
                <div className="mt-2 text-xs text-[#D97706] flex items-center gap-1" data-testid="jm-disabled-hint">
                  <AlertTriangle size={12}/> {disabledReason}
                </div>
              )}
              <div className="mt-3 flex items-center gap-2 text-xs text-[#64748B]">
                <ShieldCheck size={13} className="text-[#059669]"/>
                {t("job.noInvent")}
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        {analysis && (
          <ResultsView
            data={analysis}
            improved={improved}
            improving={improving}
            onImprove={improve}
            onUseImproved={useImproved}
            savedResult={savedResult}
            t={t}
          />
        )}
      </div>
    </div>
  );
}

function ResultsView({ data, improved, improving, onImprove, onUseImproved, savedResult, t }) {
  const a = data.analysis;
  const score = a.match_score ?? 0;
  const color = score >= 75 ? "#059669" : score >= 50 ? "#D97706" : "#DC2626";
  return (
    <div className="mt-10 space-y-6" data-testid="jm-results">
      <div className="card-classic p-6 flex flex-wrap items-center gap-6" data-testid="jm-score-card">
        <div className="relative w-32 h-32">
          <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
            <circle cx="50" cy="50" r="44" stroke="#E2DBCE" strokeWidth="8" fill="none"/>
            <circle cx="50" cy="50" r="44" stroke={color} strokeWidth="8" fill="none"
              strokeDasharray={`${(score / 100) * 276.46} 276.46`}
              strokeLinecap="round"/>
          </svg>
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-center">
              <div className="font-serif-display text-3xl font-bold" style={{color}} data-testid="jm-match-score">{score}%</div>
              <div className="text-[10px] font-mono-jb uppercase tracking-wider text-[#64748B]">{t("job.matchScore")}</div>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="font-mono-jb text-xs uppercase tracking-wider text-[#64748B]">{t("job.targetRole")}</div>
          <div className="font-serif-display text-2xl font-semibold text-[#0F172A]" data-testid="jm-job-title">{data.job_title || "—"}</div>
          <p className="mt-2 text-[#475569] leading-relaxed">{a.summary}</p>
          <Button
            onClick={onImprove}
            disabled={improving}
            className="mt-4 bg-[#881337] hover:bg-[#6B1129] text-white"
            data-testid="jm-improve-btn"
          >
            {improving ? (<><Loader2 size={16} className="mr-2 animate-spin"/> {t("job.improving")}</>) :
              (<><Wand2 size={16} className="mr-2"/> {t("job.improveBtn")}</>)}
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <ListCard testId="jm-strengths" icon={<CheckCircle2 className="text-[#059669]" size={18}/>}
          title={t("job.strengths")} items={a.strengths} emptyText={t("job.noItems")} color="#059669"/>
        <ListCard testId="jm-gaps" icon={<AlertTriangle className="text-[#D97706]" size={18}/>}
          title={t("job.gaps")} items={a.gaps} emptyText={t("job.noItems")} color="#D97706"/>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <ChipCard testId="jm-matched-kw" icon={<Zap className="text-[#0F172A]" size={16}/>}
          title={t("job.matched")} items={a.matching_keywords} tone="dark"/>
        <ChipCard testId="jm-missing-kw" icon={<Zap className="text-[#DC2626]" size={16}/>}
          title={t("job.missing")} items={a.missing_keywords} tone="danger"/>
      </div>

      <div className="card-classic p-5" data-testid="jm-ats-notes">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={18} className="text-[#0F172A]"/>
          <h3 className="font-serif-display text-xl font-semibold">{t("job.atsNotes")}</h3>
        </div>
        <p className="text-xs text-[#64748B] mb-2">{t("job.atsDisclaimer")}</p>
        {a.ats_notes?.length ? (
          <ul className="space-y-1 text-sm text-[#334155]">
            {a.ats_notes.map((n, i) => <li key={i} className="flex gap-2"><span className="text-[#0F172A]">›</span>{n}</li>)}
          </ul>
        ) : <div className="text-sm text-[#64748B]">{t("job.noItems")}</div>}
      </div>

      {/* Before/After */}
      {improved && (
        <div className="card-classic p-6" data-testid="jm-before-after">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <h3 className="font-serif-display text-2xl font-semibold">{t("job.beforeAfter")}</h3>
              <p className="text-xs text-[#64748B] mt-1">{t("job.beforeAfterHint")}</p>
            </div>
            {!savedResult && (
              <Button onClick={onUseImproved} className="bg-[#0F172A] hover:bg-[#1E293B]" data-testid="jm-use-improved">
                <FileText size={16} className="mr-2"/> {t("job.useImproved")}
              </Button>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <BeforeAfterCol title={t("job.before")} label="original" data={null} tone="muted" t={t}/>
            <BeforeAfterCol title={t("job.after")} label="improved" data={improved} tone="accent" t={t}/>
          </div>
          {savedResult && (
            <div className="mt-4 flex items-center gap-3 p-4 rounded-md bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="text-[#059669]" size={20}/>
              <div className="flex-1">
                <div className="font-semibold text-[#065F46]">{t("job.savedTitle")}</div>
                <div className="text-sm text-emerald-800">{savedResult.title}.pdf · {savedResult.credits} credits left</div>
              </div>
              <a href={`${process.env.REACT_APP_BACKEND_URL}${savedResult.download_url}?token=${encodeURIComponent(localStorage.getItem("ipdfcv_token"))}`}
                target="_blank" rel="noreferrer">
                <Button variant="outline" className="border-[#059669] text-[#059669]" data-testid="jm-saved-download">
                  {t("common.download")}
                </Button>
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ListCard({ icon, title, items, emptyText, color, testId }) {
  return (
    <div className="card-classic p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="font-serif-display text-lg font-semibold">{title}</h3>
      </div>
      {items?.length ? (
        <ul className="space-y-1.5 text-sm text-[#334155]">
          {items.map((s, i) => (
            <li key={i} className="flex gap-2">
              <span style={{color}}>●</span>{s}
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-sm text-[#64748B]">{emptyText}</div>
      )}
    </div>
  );
}

function ChipCard({ icon, title, items, tone, testId }) {
  const chipCls = tone === "danger"
    ? "bg-red-50 border border-red-200 text-[#7F1D1D]"
    : "bg-[#0F172A] text-white";
  return (
    <div className="card-classic p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="font-serif-display text-lg font-semibold">{title}</h3>
      </div>
      {items?.length ? (
        <div className="flex flex-wrap gap-1.5">
          {items.map((k, i) => (
            <span key={i} className={`inline-flex px-2.5 py-1 rounded-full text-xs font-mono-jb ${chipCls}`}>
              {k}
            </span>
          ))}
        </div>
      ) : (
        <div className="text-sm text-[#64748B]">—</div>
      )}
    </div>
  );
}

function BeforeAfterCol({ title, label, data, tone, t }) {
  const cls = tone === "accent"
    ? "border-[#0F172A] bg-white"
    : "border-[#E2DBCE] bg-[#F4F0EA]";
  return (
    <div className={`rounded-md border p-4 text-[12px] leading-5 ${cls}`} data-testid={`jm-col-${label}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-serif-display text-sm font-semibold">{title}</div>
        <span className="pill">{label}</span>
      </div>
      {data ? (
        <>
          {data.summary && (<>
            <SectionTitle>{t("cv.section.summary")}</SectionTitle>
            <p className="text-[#334155]">{data.summary}</p>
          </>)}
          {data.experience?.length > 0 && (<>
            <SectionTitle>{t("cv.section.experience")}</SectionTitle>
            {data.experience.map((e,i) => (
              <div key={i} className="mt-1">
                <div><span className="font-semibold text-[#0F172A]">{e.role}</span>{e.company && ` — ${e.company}`}</div>
                {(e.bullets||[]).filter(Boolean).map((b, bi) => (
                  <div key={bi} className="text-[#475569]">• {b}</div>
                ))}
              </div>
            ))}
          </>)}
          {data.skills?.length > 0 && (<>
            <SectionTitle>{t("cv.section.skills")}</SectionTitle>
            <p className="text-[#334155]">{data.skills.join(" · ")}</p>
          </>)}
        </>
      ) : (
        <div className="text-[#64748B] italic">{t("job.beforeNote")}</div>
      )}
    </div>
  );
}
function SectionTitle({ children }) {
  return <div className="font-serif-display font-bold text-[11px] tracking-wide text-[#881337] mt-2">{children.toUpperCase()}</div>;
}
